import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';

import { type Permissions } from 'services/constants';
import {
    sendUserSupportMessage,
    setUserSupportConversation,
    setUserSupportReferral,
} from 'services/userSupportHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetSupplierUserNameMapQuery } from 'store/api/authApi';
import { useGetReferralQuery } from 'store/api/performanceApi';
import { useGetPayerSegmentsQuery, useGetProductsMapQuery } from 'store/api/referenceApi';
import { useGetReferralDetailsQuery } from 'store/api/treatmentApi';
import {
    UNREAD_COUNTS_STALE_SECONDS,
    type UnreadCounts,
    useGetConversationFactsQuery,
    useGetMessagesQuery,
    useGetReferralConversationsQuery,
    useGetUnreadCountsQuery,
    useSendMessageMutation,
} from 'store/api/userSupportChatApi';
import { fetchAuthProfile } from 'store/auth/authThunks';
import {
    updateCurrentReferral,
    updateReferralConversations,
} from 'store/userSupport/userSupportSlice';
import { registerUser } from 'store/userSupport/userSupportThunks';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

export const usePermissionCheck = (
    permissionToCheck: (typeof Permissions)[keyof typeof Permissions],
) => {
    const permissions = useAppSelector((state) => state.auth.permissions);
    return permissions?.includes(permissionToCheck);
};

export const useReferralDetails = (referralId?: string, payerId?: string) => {
    const { data } = useGetReferralDetailsQuery(
        referralId && payerId ? { payerId, referralId } : skipToken,
    );
    return data;
};

export const useWorklistTasks = () => {
    const dispatch = useAppDispatch();
    const worklistTasks = useAppSelector((state) => state.worklist.worklistTasks);
    useEffect(() => {
        void dispatch(fetchWorklistTasks());
    }, [dispatch]);

    const { data: userNameMap } = useGetSupplierUserNameMapQuery();
    const { data: segments } = useGetPayerSegmentsQuery();
    const { data: productsMap } = useGetProductsMapQuery();

    const mappedTasks = useMemo(
        () =>
            worklistTasks?.map((task) => ({
                ...task,
                health_plan: segments?.payerSegmentMap?.[task.segment_id]?.name,
                action_taken_by: userNameMap?.[task.action_taken_by ?? ''] ?? task.action_taken_by,
                task_info: {
                    ...task.task_info,
                    product_name:
                        task.task_info?.product_name ??
                        productsMap?.[task.task_info?.product_service_oid ?? '']?.description,
                },
            })),
        [worklistTasks, userNameMap, segments?.payerSegmentMap, productsMap],
    );
    return mappedTasks;
};

export const useAuthProfile = (isOpen = true) => {
    const dispatch = useAppDispatch();
    const profile = useAppSelector((state) => state.auth.profile);
    useEffect(() => {
        void (async () => {
            if (isOpen && !profile) {
                await dispatch(fetchAuthProfile());
            }
        })();
    }, [dispatch, isOpen, profile]);

    return profile;
};

export const useUserProfile = () => {
    const dispatch = useAppDispatch();
    const userProfile = useAppSelector((state) => state.userSupport.userProfile);
    const authProfile = useAuthProfile(true);
    useEffect(() => {
        if (authProfile && !userProfile) {
            dispatch(
                registerUser({
                    details: {
                        first_name: authProfile?.firstName,
                        last_name: authProfile?.lastName,
                    },
                }),
            );
        }
    }, [authProfile, dispatch, userProfile]);

    return userProfile;
};

/**
 * Open / unread conversation counts for badges. With userSupportRestFetch on they come from the
 * unread_counts endpoint (reused for UNREAD_COUNTS_STALE_SECONDS across mounting components);
 * otherwise from the socket-fed slice.
 */
export const useUnreadCounts = ({ skip = false }: { skip?: boolean } = {}): UnreadCounts => {
    const { userSupportRestFetch } = useFlags<{ userSupportRestFetch?: boolean }>();
    const { data } = useGetUnreadCountsQuery(undefined, {
        skip: skip || !userSupportRestFetch,
        refetchOnMountOrArgChange: UNREAD_COUNTS_STALE_SECONDS,
    });
    const unreadConversationCount = useAppSelector(
        (state) => state.userSupport.unreadConversationCount,
    );
    const referralConversationCounts = useAppSelector(
        (state) => state.userSupport.referralConversationCounts,
    );
    if (userSupportRestFetch) {
        return data ?? {};
    }
    return { unreadConversationCount, referralConversationCounts };
};

export const useUserSupportMessages = () => {
    const dispatch = useAppDispatch();
    const { userSupportRestWrites, userSupportRestFetch } = useFlags<{
        userSupportRestWrites?: boolean;
        userSupportRestFetch?: boolean;
    }>();
    const conversationId = useAppSelector((state) => state.userSupport.currentConversation);
    const socketMessages = useAppSelector(
        (state) => state.userSupport.conversationMessages[conversationId ?? '']?.messages,
    );
    // Reading marks the conversation read, so every select goes to the network
    const { data: restMessages } = useGetMessagesQuery(conversationId ?? skipToken, {
        skip: !userSupportRestFetch,
        refetchOnMountOrArgChange: true,
    });
    const { data: facts } = useGetConversationFactsQuery(conversationId ?? skipToken);
    useEffect(() => {
        if (conversationId && !userSupportRestFetch) {
            dispatch(setUserSupportConversation(conversationId));
        }
    }, [conversationId, dispatch, userSupportRestFetch]);

    const [sendMessageMutation] = useSendMessageMutation();

    const sendMessageViaRest = (message: {
        conversation_id: string;
        details: { content: { message: string } };
    }) =>
        sendMessageMutation({
            conversationId: message.conversation_id,
            details: message.details,
        }).unwrap();

    const messages = socketMessages ? Object.values(socketMessages) : undefined;

    return {
        messages: userSupportRestFetch ? restMessages : messages,
        facts,
        writesViaRest: !!userSupportRestWrites,
        fetchViaRest: !!userSupportRestFetch,
        sendMessage: userSupportRestWrites
            ? sendMessageViaRest
            : (message: { conversation_id: string; details: { content: { message: string } } }) => {
                  dispatch(sendUserSupportMessage(message));
              },
    };
};

export const useUserSupportConversations = (referralId?: string) => {
    const dispatch = useAppDispatch();
    const { userSupportRestFetch } = useFlags<{ userSupportRestFetch?: boolean }>();
    const socketConversations = useAppSelector((state) => state.userSupport.referralConversations);
    // Navigating into a referral always reloads its conversations
    const { data: restConversations } = useGetReferralConversationsQuery(referralId ?? skipToken, {
        skip: !userSupportRestFetch,
        refetchOnMountOrArgChange: true,
    });
    const referralConversationCounts =
        useUnreadCounts().referralConversationCounts?.[referralId ?? ''];

    let conversationList;
    if (userSupportRestFetch) {
        // Per-conversation unread comes from is_unread on the REST list response
        conversationList = restConversations;
    } else {
        conversationList = socketConversations ? Object.values(socketConversations) : undefined;
        if (referralConversationCounts) {
            conversationList = conversationList?.map((conversation) => ({
                ...conversation,
                isUnread:
                    referralConversationCounts?.conversations[conversation.conversationId] ?? false,
            }));
        }
    }

    useEffect(() => {
        if (!referralId) {
            return undefined;
        }
        if (userSupportRestFetch) {
            dispatch(updateCurrentReferral({ referralId }));
            return () => {
                dispatch(updateCurrentReferral());
            };
        }
        dispatch(setUserSupportReferral(referralId));
        return () => {
            dispatch(updateReferralConversations({ conversations: undefined }));
            dispatch(setUserSupportReferral(null));
            dispatch(updateCurrentReferral());
        };
    }, [dispatch, referralId, userSupportRestFetch]);
    return {
        conversationList,
        referralConversationCounts,
    };
};

/** Referral could come from won or pending */
export const useSelectedReferral = ({ referralId }: { referralId: string }) => {
    const { data: supplierReferral } = useGetReferralQuery(referralId);
    const pendingReferral = useAppSelector((state) =>
        state.wonReferralList.pendingReferralData?.find(
            (referral) => referral.referralId === referralId,
        ),
    );

    return supplierReferral ? { ...supplierReferral, ...pendingReferral } : pendingReferral;
};
